import React, { useEffect, useRef, useState } from 'react';
import RPCDemo from '../components/RPCDemo';
import './PortfolioDemo.css';

const projects = [
  ['01', 'RPCDiff', 'RPC compatibility & migration tooling', 'Built a Go CLI that compares Ethereum RPC responses, classifies mismatches, and generates JSON and HTML reports for CI migration gates. Added a shadow proxy that asynchronously duplicates allowlisted reads to a candidate provider while sending transactions only to the baseline.', 'Go · Ethereum · JSON-RPC', 'https://github.com/lakshkumar06/rpcdiff'],
  ['02', 'Tempo VA Guard', 'Deposit indexing & failure recovery', 'Built a deposit-indexing library that pairs transfer events, persists deposits in SQLite, and delivers signed webhooks through a transactional outbox. Tests cover duplicate ingestion, chain rollback, retries, and crash recovery.', 'TypeScript · Rust', 'https://github.com/lakshkumar06/tempo-va-guard'],
  ['03', 'Agreed', 'Contract versioning & escrow', 'Built contract revision history, line diffs, and unanimous approval workflows backed by SQLite. Integrated Solana programs for participant approvals and milestone escrow, with contract documents stored on IPFS.', 'Rust · Solana / Anchor · React', 'https://github.com/lakshkumar06/Agreed'],
  ['04', 'Lattice', 'Credit-pool accounting', 'Implemented a Solidity lending pool with share pricing, cash-basis interest, borrowing limits, and default write-downs. A 26-test Foundry suite checks repayment, liquidity constraints, access control, and losses passed through to shareholders.', 'Solidity · Foundry · React', 'https://github.com/lakshkumar06/lattice'],
  ['05', 'Resona', 'Voice embeddings & authentication analytics', 'Built a voice-processing service using SpeechBrain’s ECAPA-TDNN speaker embeddings, with a Solidity registry for voice commitments and storage references. Implemented event indexing and per-user threshold recommendations based on recorded authentication outcomes.', 'Python · FastAPI · Solidity', 'https://github.com/lakshkumar06/Resona'],
  ['06', 'Araxia', 'Banking workflow integration', 'Built the banking transfer interface for a team authorization project, connecting action creation, passkey approval, and execution to the existing backend. Exposed denied and uncertain transfer outcomes and linked receipts to the protocol inspection console.', 'TypeScript · Next.js · WebAuthn', 'https://github.com/kushwahaamar-dev/araxia'],
];

const experience = [
  ['Jan — Aug 2026', 'Software Engineering Intern', 'Gemini', [
    <>Built a production feature-flag governance system spanning transactional write orchestration, append-only audit logs, dual-control approvals, and legacy-system migration; governed <strong> 2,000+ </strong>flags across all existing systems, enforcing auditable ownership and approval workflows for safety-critical flags without breaking existing integrations</>,
    <>Owned the TrueML (a third-party collections vendor) integration end-to-end, implementing scheduled account placement, payment synchronization, and retraction workflows – reduced average total delinquent credit card debt by <strong>~$200K </strong> per month</>,
    <>Rewrote MX merchant enrichment pipeline and cache layer in Scala, fixing 4 years of corrupted data across <strong>~7 Million </strong>merchant records and reducing third-party MX API calls by <strong>~65%</strong> through smarter cache invalidation logic</>,
    <>Integrated GIACT gAuthenticate to close a bank-account authentication gap across <strong>~7,080 </strong>legacy credit-card users by implementing verification-state tracking, and enforcing deadline-based account re-linking</>,
    <>Built a greenfield Scala microservice on Kubernetes that monitored Kalshi, Polymarket, and Gemini markets, detected cross-platform arbitrage opportunities, and automatically generated and published visual alerts to <strong>@GeminiMarkets</strong> on X</>,
  ]],
  ['Jan 2024 — Nov 2025', 'Undergraduate Research Assistant', 'Texas Tech University', [
    <>Built and deployed a Retrieval-Augmented Generation (RAG) Al application for USAF military contractors, achieving a <strong>50% </strong>reduction in procurement time and enhancing efficiency by over <strong>40%</strong></>,
    <>Designed and implemented scalable data preprocessing pipelines to support machine learning model development using LangChain, ChromaDB, PostgreSQL, and DSPyto deliver a robust, integrated solution</>,
    <>Conducted backend experiments to evaluate large language model configurations, leveraging metrics like accuracy,
    precision, recall, and F1 score for performance benchmarking and integrated with cloud tools for reproducibility and
    version control</>
  ]],
  ['May — Aug 2024', 'Software Developer Intern', 'Primitive', [
    <>Developed and maintained scalable, web applications using JavaScript, PHP, and C#, serving <strong>10K+</strong> users, in a microservices-based architecture</>,
    <>Collaborated with product teams to optimize performance, containerize services using Docker, and integrate backend functionality into platforms such as WordPress and Bind CMS</>,
    <>Improved user experience through secure RESTful API development, performance analytics, and real-time content updates</>
  ]],
];

const hackathonWins = [
  'ETHGlobal',
  'HackUTD',
  'Colosseum x College_xyz',
  'EthDenver',
  'NASA Space Apps',
  'N3XUS',
  'HackWest',
  'Hacklahoma 2025',
  'RiverHacks',
];

const articles = [
  {
    title: 'Interning at Gemini',
    date: 'Aug 15, 2026',
    platform: 'X',
    image: `${process.env.PUBLIC_URL}/blog1.png`,
    link: 'https://x.com/klakshh/status/2088680426302820756',
  },
];

const affiliations = [
  ['ttu.jpeg', 'Texas Tech University'],
  ['ferrari.jpeg', 'Ferrari'],
  ['college xyz.jpeg', 'College.xyz'],

];

const Arrow = () => <span aria-hidden="true" className="arrow">↗︎</span>;
const Heading = ({ children }) => <div className="section-heading"><h2>{children}</h2></div>;

function FeaturedProject() {
  const [, name, type, description, stack, link] = projects[0];
  return <article className="featured-project">
    <div className="featured-project-intro">
      <div className="featured-project-title"><span className="muted">{type}</span><h3><a href={link} target="_blank" rel="noreferrer">{name}<Arrow /></a></h3><span className="project-stack">{stack}</span></div>
      <p>{description}</p>
    </div>
    <RPCDemo />
  </article>;
}

function ExperienceItem({ item, initiallyOpen }) {
  const [date, role, company, contributions] = item;
  const [open, setOpen] = useState(initiallyOpen);

  return <div className={`experience-item${open ? ' is-open' : ''}`}>
    <button className="experience-row" type="button" aria-expanded={open} onClick={() => setOpen(value => !value)}>
      <span className="muted">{date}</span><strong>{role}</strong><span>{company}</span><span className="experience-toggle" aria-hidden="true">+</span>
    </button>
    <div className="experience-details" aria-hidden={!open}><div className="experience-details-inner"><ul>{contributions.map((contribution, index) => <li key={index}>{contribution}</li>)}</ul></div></div>
  </div>;
}

function BackgroundArtwork({ onMapReady, onSkylineReady }) {
  return <>
        <img className="backdrop-map" src={`${process.env.PUBLIC_URL}/mapkarachi.png`} alt="" draggable="false" onLoad={onMapReady} onError={onMapReady} />
        <img className="backdrop-skyline" src={`${process.env.PUBLIC_URL}/HeroBG.png`} alt="" draggable="false" onLoad={onSkylineReady} onError={onSkylineReady} />
  </>;
}

function InteractiveBackground() {
  const spotlightRef = useRef(null);
  const [artworkLoaded, setArtworkLoaded] = useState({ map: false, skyline: false });
  const markMapReady = () => setArtworkLoaded(value => value.map ? value : { ...value, map: true });
  const markSkylineReady = () => setArtworkLoaded(value => value.skyline ? value : { ...value, skyline: true });

  useEffect(() => {
    const spotlight = spotlightRef.current;
    let frame = null;
    let x = 0;
    let y = 0;
    const hide = () => {
      cancelAnimationFrame(frame);
      frame = null;
      spotlight.style.opacity = '0';
    };
    const move = event => {
      if (event.pointerType === 'touch') {
        hide();
        return;
      }
      x = event.clientX;
      y = event.clientY;
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        spotlight.style.setProperty('--cursor-x', `${x}px`);
        spotlight.style.setProperty('--cursor-y', `${y}px`);
        spotlight.style.opacity = '1';
        frame = null;
      });
    };
    window.addEventListener('pointermove', move, { passive: true });
    document.documentElement.addEventListener('pointerleave', hide);
    window.addEventListener('blur', hide);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('pointermove', move);
      document.documentElement.removeEventListener('pointerleave', hide);
      window.removeEventListener('blur', hide);
    };
  }, []);

  const isReady = artworkLoaded.map && artworkLoaded.skyline;

  return <div className={`portfolio-backdrop${isReady ? ' is-ready' : ''}`} aria-hidden="true">
    <div className="backdrop-layer"><BackgroundArtwork onMapReady={markMapReady} onSkylineReady={markSkylineReady} /></div>
    <div ref={spotlightRef} className="backdrop-spotlight backdrop-layer"><BackgroundArtwork /></div>
  </div>;
}

export default function OnePagePortfolio() {
  return <main className="character-portfolio">
    <InteractiveBackground />
    <section id="lakshh" className="section-shell section-block">
    
      <div className="intro-title"><h1>Lakshh</h1><div className="intro-map-note"><span className="location-pin" aria-hidden="true"><svg viewBox="0 0 24 24" focusable="false"><path d="M12 21s7-6.1 7-12A7 7 0 0 0 5 9c0 5.9 7 12 7 12Z" /><circle cx="12" cy="9" r="2.2" /></svg></span><span>Lubbock<br /><span className="intro-map-caption">Texas</span></span></div></div>
      <div className="affiliations" aria-label="Affiliations">{affiliations.map(([file, name]) => <div className="affiliation-tile" key={file}><img src={`${process.env.PUBLIC_URL}/affiliations/${encodeURIComponent(file)}`} alt={name} /></div>)}</div>
      <div className="intro-story">
        <p className="intro-lead">I am a software engineer passionate about building shit in fintech, blockchain, capital markets, and agentic finance</p>
        <div className="intro-background"><p>I started my career as a designer when I worked with local business and later a marketing firm. Then I got into blockchain and later fintech in general via my school’s blockchain club. </p><p>Now there are hackathons, flights across the country, and a lots of interesting people.</p><p>That's about it</p></div>
      </div>
    </section>

    <section id="experience" className="section-shell section-block"><Heading>Experience</Heading><div className="experience-list">{experience.map(item => <ExperienceItem item={item} initiallyOpen={false} key={item[2]} />)}</div></section>

    <section id="work" className="section-shell section-block"><Heading>Featured work</Heading><FeaturedProject /><div className="project-list">{projects.slice(1).map(([, name, type, description, stack, link]) => <a className="project-row project-row-text" href={link} target="_blank" rel="noreferrer" key={name}><div className="project-copy"><div className="project-identity"><span className="muted">{type}</span><h3>{name}</h3><span className="project-stack">{stack}</span></div><p>{description}</p></div><Arrow /></a>)}</div></section>

    <section id="writing" className="section-shell section-block"><Heading>Blog</Heading><div className="article-list">{articles.map(article => <a className="article-row" href={article.link} target="_blank" rel="noreferrer" key={article.link}>
      <div className="article-image"><img src={article.image} alt="A group looking out over the city at night" loading="lazy" /></div>
      <div className="article-copy"><div className="article-meta"><span className="article-platform"><img src={`${process.env.PUBLIC_URL}/X.svg`} alt={article.platform} /></span><span>{article.date}</span></div><h3>{article.title}</h3><span className="article-read">Read the story <span aria-hidden="true">↗︎</span></span></div>
      <Arrow />
    </a>)}</div></section>

    <section id="hackathons" className="section-shell section-block"><Heading>Hackathon wins</Heading><div className="hackathon-layout"><div className="hackathon-note"><span className="weekend-mark" aria-hidden="true">✳</span><p>A few weekends<br /> well spent.</p><span>Flying across the country.<br />Building things. Meeting people.</span></div><div className="wins-list">{hackathonWins.map(win => <div className="win-row" key={win}><strong>{win}</strong><span className="win-mark" aria-label="Win">*</span></div>)}</div></div></section>

    <section id="contact" className="contact-section"><div className="section-shell contact-inner"><h2>Have something<br /><em>worth building?</em></h2><div className="contact-links"><a href="https://github.com/lakshkumar06" target="_blank" rel="noreferrer"><span>github.com/lakshkumar06</span><span className="contact-arrow" aria-hidden="true">↗︎</span></a><a href="https://www.linkedin.com/in/kumar-laksh" target="_blank" rel="noreferrer"><span>linkedin.com/in/kumar-laksh</span><span className="contact-arrow" aria-hidden="true">↗︎</span></a><a href="https://www.x.com/kumarlaksh06" target="_blank" rel="noreferrer"><span>x.com/kumarlaksh06</span><span className="contact-arrow" aria-hidden="true">↗︎</span></a><a href="https://t.me/lakshkumar06" target="_blank" rel="noreferrer"><span>t.me/lakshkumar06</span><span className="contact-arrow" aria-hidden="true">↗︎</span></a></div></div></section>
  </main>;
}
